// validator-controller.js
import { validatorRegistry } from "./validator-registry";
import { schemaBuilder } from "./zod-schema-builder";
import { ValidatorInterface } from "./validator-interface";
import Alpine from "alpinejs";

export const validatorController = {
    forms: new Map(),

    async validateField(field) {
        const form = field.closest("form");
        if (!form) return false; // Ensure field is inside a form
        const formData = this.forms.get(form);
        // Check if form is initialized and scope exists
        if (!formData || typeof formData.scope?.errors === "undefined") return false;

        const value = field.value;
        const name = field.getAttribute("name");
        if (!name) return false; // Field needs a name attribute

        // First validate using Zod schema
        const fieldSchema = formData.schemas.get(field);
        if (!fieldSchema) return true; // No schema for this field, consider valid

        try {
            fieldSchema.parse(value);
            this._clearFieldError(form, field);
        } catch (err) {
            // Assuming Zod error format
            const errorMessage = err.errors?.[0]?.message || "Invalid input";
            this._setFieldError(form, field, errorMessage);
            return false; // Stop validation if Zod fails
        }

        // If Zod validation passes, run external validators (async or otherwise)
        const rules = this._getFieldRules(field);
        for (const rule of rules) {
            if (validatorRegistry.has(rule.name)) {
                const validator = validatorRegistry.get(rule.name);

                // Only call validateExternal if it's overridden (for async/external validators)
                if (validator.validateExternal !== ValidatorInterface.prototype.validateExternal) {
                    try {
                        const isValid = await validator.validateExternal(value, rule.options);
                        if (!isValid) {
                            this._setFieldError(form, field, validator.getErrorMessage(field, rule.options));
                            return false; // Stop on first external validation failure
                        }
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(`External validation failed for rule '${rule.name}':`, error);
                        this._setFieldError(form, field, validator.getErrorMessage(field, rule.options) || "Validation check failed");
                        return false; // Stop on error
                    }
                }
            }
        }

        // All validations passed
        this._clearFieldError(form, field);
        return true;
    },

    async validateForm(form) {
        const formData = this.forms.get(form);
        if (!formData) return false;

        let isFormValid = true;
        const fields = form.querySelectorAll("[data-zod-type]");

        // Use Promise.all to run validations concurrently where possible
        const validationPromises = [];
        fields.forEach(field => {
            validationPromises.push(this.validateField(field));
        });

        const results = await Promise.all(validationPromises);
        isFormValid = results.every(isValid => isValid);

        return isFormValid;
    },

    initializeForm(form, scope) {
        // Skip if already initialized
        if (this.forms.has(form)) return;

        // Wait for Alpine to initialize the scope
        if (!scope || typeof scope.errors === "undefined") {
            // Use requestAnimationFrame for better timing coordination with Alpine
            requestAnimationFrame(() => {
                const currentScope = Alpine.$data(form);
                if (currentScope) {
                    this.initializeForm(form, currentScope);
                } else {
                    // Fallback if scope still not found after delay
                    // eslint-disable-next-line no-console
                    console.warn("Alpine scope not found for form", form);
                }
            });
            return;
        }

        const fields = form.querySelectorAll("[data-zod-type]");
        const schemas = new Map();

        fields.forEach(field => {
            try {
                schemas.set(field, schemaBuilder.buildFieldSchema(field));
                const name = field.getAttribute("name");
                if (name) {
                    // Initialize error state in Alpine scope if not already present
                    if (!(name in scope.errors)) {
                        scope.errors[name] = null;
                    }
                }
                // Remove previous listeners if any to prevent duplicates
                field.removeEventListener("blur", this._handleBlur);
                field.removeEventListener("input", this._handleInput);

                // Add blur listener for validation
                field.addEventListener("blur", this._handleBlur.bind(this));
                // Add input listener for clearing errors on change
                field.addEventListener("input", this._handleInput.bind(this));
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error building schema or attaching listener for field:", field, error);
            }
        });

        // Store form data
        this.forms.set(form, {
            schemas,
            scope
        });

        // Handle form submission
        form.removeEventListener("submit", this._handleSubmit);
        form.addEventListener("submit", this._handleSubmit.bind(this));

        // Add novalidate attribute to prevent default browser validation
        form.setAttribute("novalidate", "");
    },

    // Bound event handlers to maintain 'this' context
    _handleBlur(event) {
        this.validateField(event.target);
    },

    _handleInput(event) {
        // Clear error on input for better UX
        const field = event.target;
        const form = field.closest("form");
        if (form && this.forms.has(form)) {
            this._clearFieldError(form, field);
        }
    },

    async _handleSubmit(event) {
        const form = event.target;
        const isFormValid = await this.validateForm(form);

        if (!isFormValid) {
            event.preventDefault(); // Prevent submission if validation fails
            // eslint-disable-next-line no-console
            console.log("Form validation failed");
            // Optional: focus the first invalid field
            const firstInvalidField = form.querySelector('[aria-invalid="true"]');
            firstInvalidField?.focus();
        }
    },

    _setFieldError(form, field, message) {
        const formData = this.forms.get(form);
        const name = field.getAttribute("name");
        if (formData?.scope?.errors && name) {
            // Store the error message as an array to match formError.ftl expectation
            formData.scope.errors[name] = [message];
            field.setAttribute("aria-invalid", "true");
        }
    },

    _clearFieldError(form, field) {
        const formData = this.forms.get(form);
        const name = field.getAttribute("name");
        if (formData?.scope?.errors && name && formData.scope.errors[name] !== null) {
            // Clear the error state for Alpine x-show
            formData.scope.errors[name] = null;
            field.removeAttribute("aria-invalid");
        }
    },

    _getFieldRules(field) {
        const rulesAttr = field.getAttribute("data-zod-rules");
        if (!rulesAttr) return [];

        return rulesAttr
            .split(",")
            .map(ruleString => {
                const parts = ruleString.trim().split("=");
                const name = parts[0];
                const value = parts[1]; // Parameter for the rule (e.g., minLength=5)
                const message = parts[2]; // Custom message (optional)
                // Pass necessary options, potentially including labelText if needed by getErrorMessage
                const labelText = schemaBuilder._getFieldLabelText(field); // Reuse label getter
                return {
                    name,
                    options: { value, message, labelText }
                };
            })
            .filter(rule => rule.name); // Filter out empty rule names
    },

    destroyForm(form) {
        if (!this.forms.has(form)) return;

        const fields = form.querySelectorAll("[data-zod-type]");

        // Remove event listeners
        fields.forEach(field => {
            field.removeEventListener("blur", this._handleBlur);
            field.removeEventListener("input", this._handleInput);
        });
        form.removeEventListener("submit", this._handleSubmit);

        // Clear stored data
        this.forms.delete(form);
    }
};

// Add cleanup for Alpine components
document.addEventListener("alpine:destroy", el => {
    if (el.hasAttribute("x-validator") && el.tagName === "FORM") {
        validatorController.destroyForm(el);
    }
});
